import React, { useCallback, useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { Merchant, PatchMerchantPayload, ReferrerMerchantType } from 'src/resources/api/merchant/types.api';
import { PortalConfiguration } from 'src/resources/api/cms/types.api';
import { getMerchantByIdAPI, patchMerchantAPI } from 'src/resources/api/merchant/index.api';
import useAuth from 'src/hooks/useAuth';
import useCarbonUser from 'src/hooks/useCarbonUser';
import { CarbonUser } from 'src/resources/api/customers/types.api';

//
//

type ReferrerMerchantConfig = {
  merchantCode: string;
  referrerMerchantRelationshipType: ReferrerMerchantType;
  signUp: {
    title: string;
  };
}

type AdminUser = { merchantId: string; role: string };

type MerchantContextType = {
  merchant: Merchant | null;
  isMerchantLoading: boolean;
  config: PortalConfiguration | null;
  getMerchantUsers: () => void;
  merchantUsers: Merchant[] | null;
  getMerchantConfigByCode: (code?: string) => ReferrerMerchantConfig | undefined;
  updateMerchant: (merchantCode: string, data: PatchMerchantPayload) => Promise<void>;
  getMerchant: (user: CarbonUser | null, merchantId?: string) => Promise<void>;
  getAdminRelationshipFromUser: (user: CarbonUser, merchantId?: string) => AdminUser | null;
};

export const MerchantContext = React.createContext<MerchantContextType>({
  merchant: null,
  merchantUsers: [],
  config: null,
  isMerchantLoading: false,
  getMerchantUsers: () => {},
  getMerchantConfigByCode: () => undefined,
  updateMerchant: async () => {},
  getMerchant: async () => {},
  getAdminRelationshipFromUser: () => null,
});

type MerchantProviderProps = {
  children: React.ReactNode;
};

//

export const MerchantProvider = ({ children }: MerchantProviderProps) => {
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [merchantIsLoading, setMerchantIsLoading] = useState(true);
  const [merchantUsers, setMerchantUsers] = useState<Merchant[] | null>(null);
  const [merchantConfig, setMerchantConfig] = useState<PortalConfiguration | null>(null);

  const { carbonUser } = useCarbonUser();
  const { isAuthenticated } = useAuth();

  const getAdminRelationshipFromUser = (user: CarbonUser, merchantId?: string) => {
    const isStoredMerchantBelongsToUser = user?.merchantUsers?.find(
      (user) => user?.merchantId === merchantId
    );

    if (isStoredMerchantBelongsToUser) {
      return isStoredMerchantBelongsToUser;
    }

    return (
      user?.merchantUsers?.find((item) => item.role === 'ADMIN' || item.role === 'SUPER_USER') ||
      null
    );
  };

  //

  const getMerchantUsers = async () => {
    if (carbonUser?.merchantUsers) {
      const merchantIds = carbonUser?.merchantUsers?.map((user) => user?.merchantId);

      if (!merchantIds?.length) {
        setMerchantIsLoading(false);
        return;
      };

      const records = await Promise.all(
        merchantIds.map(async (record: string) => {
          const response = await getMerchantByIdAPI(record, true);

          if (!response?.id) return;

          return response;
        })
      );

      setMerchantUsers(records.filter((v) => !!v?.id) as Merchant[]);
    }
  };

  const getMerchant = useCallback(async (user: CarbonUser | null, id?: string) => {
    if (!user?.id || !user?.merchantUsers?.length) {
      setMerchantIsLoading(false);
      return;
    }

    const adminUser = getAdminRelationshipFromUser(user, id);

    if (!adminUser?.merchantId) {
      setMerchantIsLoading(false);
      return;
    };

    try {
      const result = await getMerchantByIdAPI(adminUser.merchantId, true);

      if (!result?.code) {
        setMerchantIsLoading(false);
        return;
      };

      try {
          setMerchantConfig({
            // defaults, remove when they are will be received from the backend
            minimumCo2KgAmountToPayByInvoice: 50000,
            maximumCo2KgAmountToPayByCard: 250000,
          });
      } catch (error) {
        setMerchantConfig({
          // defaults, remove when they are will be received from the backend
          minimumCo2KgAmountToPayByInvoice: 50000,
          maximumCo2KgAmountToPayByCard: 250000,
        });
      }

      setMerchant(result);
    } finally {
      setMerchantIsLoading(false);
    }
  }, []);

  //

  useEffect(() => {
    if (carbonUser && isAuthenticated) {
      const merchantFromLocalStorage = localStorage.getItem('storedMerchantId');
      let storedMerchantId = null;

      if (merchantFromLocalStorage) {
        try {
          storedMerchantId = JSON.parse(merchantFromLocalStorage);
        } catch (error) {
          console.error(error);
        }
      }

      getMerchant(carbonUser, storedMerchantId);
    } else {
      setMerchant(null);
      setMerchantIsLoading(false);
    }
  }, [carbonUser, carbonUser?.id, getMerchant, isAuthenticated]);

  //

  const getMerchantConfigByCode = (code?: string): ReferrerMerchantConfig | undefined => {
    if (!code) return;

    return referrerMerchantConfig.find((config) => config.merchantCode === code);
  };

  const updateMerchant = async (code: string, data: PatchMerchantPayload) => {
    if (!merchant?.code) return;

    // FIXME: backend needs pledgeTaken as required param
    const response = await patchMerchantAPI(code, { ...data, pledgeTaken: !!merchant?.pledgeTaken });

    if (!response) return;

    enqueueSnackbar({ variant: 'success', message: 'Merchant settings updated successfully' });

    setMerchant((prev) => (prev === null ? null : { ...prev, ...response }));
  };

  //

  return (
    <MerchantContext.Provider
      value={{
        merchant,
        merchantUsers,
        isMerchantLoading: merchantIsLoading,
        config: merchantConfig,
        getMerchant,
        getMerchantUsers,
        getAdminRelationshipFromUser,
        getMerchantConfigByCode,
        updateMerchant,
      }}
    >
      {children}
    </MerchantContext.Provider>
  );
};

//

const referrerMerchantConfig = [
  {
    merchantCode: 'BUS_NZ_EVENT_DEMO',
    referrerMerchantRelationshipType: ReferrerMerchantType.EVENT,
    signUp: { title: 'Sign up to our Corporate magic programme' },
  },
  {
    merchantCode: 'BUS_NZ_KIIO_DEMO_ACCOUNT',
    referrerMerchantRelationshipType: ReferrerMerchantType.EVENT,
    signUp: { title: 'Sign up to our Corporate magic programme' },
  },
  {
    merchantCode: 'BUS_AE_EXPO_CITY_DUBAI',
    referrerMerchantRelationshipType: ReferrerMerchantType.PARTNER,
    signUp: { title: 'Sign up to the Expo City Corporate Travel programme' },
  },
  {
    merchantCode: 'BUS_SG_CHANGI_AIRPORT',
    referrerMerchantRelationshipType: ReferrerMerchantType.PARTNER,
    signUp: { title: '' },
  },
  {
    merchantCode: 'BUS_NZ_CARBONCLICK',
    referrerMerchantRelationshipType: ReferrerMerchantType.PARTNER,
    signUp: { title: '' },
  },
  {
    merchantCode: 'BUS_GB_HELEN_WEBB_EVENTS',
    referrerMerchantRelationshipType: ReferrerMerchantType.EVENT,
    signUp: { title: '' },
  },
];
